import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ArrowLeft, Search, Loader2, Link2, Check, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { formatFactorValue } from '../lib/format';
import { EMISSION_CATEGORIES, getCategoryLabel } from '../lib/constants';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CustomMappingFormPage() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    category: '',
    sub_category: '',
    type: '',
    sub_type: '',
    desired_emission_factor_id: null,
    additional_information: ''
  });

  // EF Search state
  const [efSearchCategory, setEfSearchCategory] = useState('');
  const [efSearchText, setEfSearchText] = useState('');
  const [efSearchId, setEfSearchId] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedEF, setSelectedEF] = useState(null);

  // Sub-category autocomplete state for EF Search
  const [efSubCategories, setEfSubCategories] = useState([]);
  const [efSearchSubCategory, setEfSearchSubCategory] = useState('');
  const [showEfSubCatDropdown, setShowEfSubCatDropdown] = useState(false);
  const [filteredEfSubCategories, setFilteredEfSubCategories] = useState([]);
  const efSubCatInputRef = useRef(null);
  const efSubCatDropdownRef = useRef(null);

  // Fetch sub-categories for EF search when component mounts or search category changes
  useEffect(() => {
    const fetchSubCategories = async () => {
      try {
        const params = new URLSearchParams();
        if (efSearchCategory) params.append('category', efSearchCategory);
        const response = await axios.get(`${API_URL}/emission-factors/sub-categories/list?${params}`);
        setEfSubCategories(response.data);
        setFilteredEfSubCategories(response.data);
      } catch (error) {
        console.error('Failed to fetch sub-categories:', error);
      }
    };
    fetchSubCategories();
  }, [efSearchCategory]);

  // Filter sub-categories based on input
  useEffect(() => {
    if (efSearchSubCategory) {
      const filtered = efSubCategories.filter(sc => 
        sc.toLowerCase().includes(efSearchSubCategory.toLowerCase())
      );
      setFilteredEfSubCategories(filtered);
    } else {
      setFilteredEfSubCategories(efSubCategories);
    }
  }, [efSearchSubCategory, efSubCategories]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        efSubCatDropdownRef.current && 
        !efSubCatDropdownRef.current.contains(event.target) &&
        efSubCatInputRef.current &&
        !efSubCatInputRef.current.contains(event.target)
      ) {
        setShowEfSubCatDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search emission factors
  const searchEmissionFactors = async () => {
    if (!efSearchCategory && !efSearchSubCategory && !efSearchText && !efSearchId) {
      toast.error('Please enter a search criteria (category, sub category, text, or EF ID)');
      return;
    }

    setSearchLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('status', 'approved');
      params.append('page_size', '200');
      if (efSearchCategory) params.append('category', efSearchCategory);

      // If searching by ID, use the search parameter
      if (efSearchId) {
        params.append('search', efSearchId);
      } else if (efSearchText) {
        params.append('search', efSearchText);
      } else if (efSearchSubCategory) {
        params.append('search', efSearchSubCategory);
      }

      const response = await axios.get(`${API_URL}/emission-factors?${params}`);
      let results = response.data.items || [];
      
      // Additional client-side filtering for sub-category
      if (efSearchSubCategory && !efSearchId) {
        const subCatLower = efSearchSubCategory.toLowerCase();
        results = results.filter(ef => 
          ef.sub_category?.toLowerCase().includes(subCatLower)
        );
      }
      
      // Additional client-side filtering for text search
      if (efSearchText && !efSearchId) {
        const searchLower = efSearchText.toLowerCase();
        results = results.filter(ef => 
          (ef.sub_category?.toLowerCase().includes(searchLower)) ||
          (ef.type?.toLowerCase().includes(searchLower)) ||
          (ef.sub_type?.toLowerCase().includes(searchLower))
        );
      }
      
      setSearchResults(results.slice(0, 50)); // Limit to 50 results
      
      if (results.length === 0) {
        toast.info('No emission factors found matching your criteria');
      }
    } catch (error) {
      toast.error('Failed to search emission factors');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSelectEF = (ef) => {
    setSelectedEF(ef);
    setFormData(prev => ({
      ...prev,
      desired_emission_factor_id: ef.emission_factor_id
    }));
  };

  const handleSubmit = async () => {
    if (!formData.category) {
      toast.error('Please select a Category');
      return;
    }
    if (!formData.sub_category) {
      toast.error('Please enter a Sub Category');
      return;
    }
    if (!formData.desired_emission_factor_id) {
      toast.error('Please select an Emission Factor');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/custom-mappings`, {
        category: formData.category,
        sub_category: formData.sub_category,
        type: formData.type || null,
        sub_type: formData.sub_type || null,
        desired_emission_factor_id: formData.desired_emission_factor_id,
        additional_information: formData.additional_information.trim() || null
      });
      toast.success('Custom mapping submitted for approval');
      navigate('/custom-mappings');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create custom mapping');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="custom-mapping-form-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/custom-mappings')}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-3xl font-bold text-foreground">Create Custom Mapping</h1>
            <p className="text-muted-foreground mt-1">Map a custom category combination to an existing emission factor</p>
          </div>
        </div>

        {/* Custom Category Details */}
        <Card className="border-gray-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5" />
              Custom Category Details
            </CardTitle>
            <CardDescription>Define the custom category, sub-category, type and sub-type for this mapping</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="category">Category <span className="text-red-500">*</span></Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                >
                  <SelectTrigger id="category" data-testid="custom-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {EMISSION_CATEGORIES.map(opt => (
                      <SelectItem key={opt.value} value={opt.label}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="sub_category">Sub Category <span className="text-red-500">*</span></Label>
                <Input
                  id="sub_category"
                  value={formData.sub_category}
                  onChange={(e) => setFormData(prev => ({ ...prev, sub_category: e.target.value }))}
                  placeholder="Enter sub category"
                  data-testid="custom-sub-category-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="type">Type</Label>
                <Input
                  id="type"
                  value={formData.type}
                  onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                  placeholder="Enter type (optional)"
                  data-testid="custom-type-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sub_type">Sub Type</Label>
                <Input
                  id="sub_type"
                  value={formData.sub_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, sub_type: e.target.value }))}
                  placeholder="Enter sub type (optional)"
                  data-testid="custom-sub-type-input"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="additional_information">Additional Information</Label>
                <Textarea
                  id="additional_information"
                  rows={3}
                  value={formData.additional_information}
                  onChange={(e) => setFormData(prev => ({ ...prev, additional_information: e.target.value }))}
                  placeholder="Optional: why does this custom mapping belong to the selected emission factor? Approvers and auditors will read this."
                  data-testid="custom-additional-information-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* EF Search */}
        <Card className="border-gray-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="w-5 h-5" />
              Search & Select Emission Factor
            </CardTitle>
            <CardDescription>Search for an emission factor by category, text, or EF ID</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="space-y-2">
                <Label>Filter by Category</Label>
                <Select value={efSearchCategory || "all"} onValueChange={(v) => setEfSearchCategory(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="ef-search-category">
                    <SelectValue placeholder="Any category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any category</SelectItem>
                    {EMISSION_CATEGORIES.map(opt => (
                      <SelectItem key={opt.value} value={opt.label}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 relative">
                <Label>Filter by Sub Category</Label>
                <div className="relative">
                  <Input
                    ref={efSubCatInputRef}
                    value={efSearchSubCategory}
                    onChange={(e) => setEfSearchSubCategory(e.target.value)}
                    onFocus={() => setShowEfSubCatDropdown(true)}
                    placeholder="Select or type..."
                    data-testid="ef-search-sub-category"
                    autoComplete="off"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                    onClick={() => setShowEfSubCatDropdown(!showEfSubCatDropdown)}
                  >
                    <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${showEfSubCatDropdown ? 'rotate-180' : ''}`} />
                  </Button>
                </div>
                {showEfSubCatDropdown && filteredEfSubCategories.length > 0 && (
                  <div 
                    ref={efSubCatDropdownRef}
                    className="absolute z-50 w-full mt-1 max-h-60 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg"
                  >
                    {filteredEfSubCategories.slice(0, 50).map((sc, index) => (
                      <div
                        key={index}
                        className={`px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm ${efSearchSubCategory === sc ? 'bg-blue-50 text-blue-700' : ''}`}
                        onClick={() => {
                          setEfSearchSubCategory(sc);
                          setShowEfSubCatDropdown(false);
                        }}
                        data-testid={`ef-sub-category-option-${index}`}
                      >
                        {sc}
                      </div>
                    ))}
                    {filteredEfSubCategories.length > 50 && (
                      <div className="px-3 py-2 text-xs text-muted-foreground border-t">
                        Showing first 50 results. Type to filter more...
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>Search by Text</Label>
                <Input
                  value={efSearchText}
                  onChange={(e) => setEfSearchText(e.target.value)}
                  placeholder="Type, sub type..."
                  data-testid="ef-search-text"
                />
              </div>
              <div className="space-y-2">
                <Label>Search by EF ID</Label>
                <Input
                  value={efSearchId}
                  onChange={(e) => setEfSearchId(e.target.value)}
                  placeholder="Enter EF ID"
                  data-testid="ef-search-id"
                />
              </div>
              <div className="space-y-2">
                <Label>&nbsp;</Label>
                <Button 
                  onClick={searchEmissionFactors} 
                  disabled={searchLoading} 
                  className="w-full"
                  data-testid="ef-search-btn"
                >
                  {searchLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
                  Search
                </Button>
              </div>
            </div>

            {/* Selected EF */}
            {selectedEF && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="font-semibold text-green-800 flex items-center gap-2">
                  <Check className="w-5 h-5" />
                  Selected Emission Factor:
                </p>
                <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-green-700">
                  <div><strong>EF ID:</strong> #{selectedEF.emission_factor_id}</div>
                  <div><strong>Category:</strong> {getCategoryLabel(selectedEF.category)}</div>
                  <div><strong>Sub Category:</strong> {selectedEF.sub_category}</div>
                  <div><strong>Type:</strong> {selectedEF.type || '—'}</div>
                  <div><strong>Country:</strong> {selectedEF.country}</div>
                  <div><strong>CO2e:</strong> {formatFactorValue(selectedEF.co2e)}</div>
                  <div><strong>Input Unit:</strong> {selectedEF.input_unit}</div>
                  <div><strong>Is Spend Based:</strong> {selectedEF.is_spend_based ? 'Yes' : 'No'}</div>
                </div>
              </div>
            )}

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <div className="max-h-96 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="text-xs">EF ID</TableHead>
                        <TableHead className="text-xs">Category</TableHead>
                        <TableHead className="text-xs">Sub Category</TableHead>
                        <TableHead className="text-xs">Type</TableHead>
                        <TableHead className="text-xs">Sub Type</TableHead>
                        <TableHead className="text-xs">Country</TableHead>
                        <TableHead className="text-xs">CO2e</TableHead>
                        <TableHead className="text-xs">Input Unit</TableHead>
                        <TableHead className="text-xs">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {searchResults.map((ef) => (
                        <TableRow 
                          key={ef.emission_factor_id} 
                          className={`cursor-pointer hover:bg-gray-50 ${selectedEF?.emission_factor_id === ef.emission_factor_id ? 'bg-green-50' : ''}`}
                        >
                          <TableCell className="text-xs font-mono">{ef.emission_factor_id}</TableCell>
                          <TableCell className="text-xs">{getCategoryLabel(ef.category)}</TableCell>
                          <TableCell className="text-xs">{ef.sub_category}</TableCell>
                          <TableCell className="text-xs">{ef.type || '—'}</TableCell>
                          <TableCell className="text-xs">{ef.sub_type || '—'}</TableCell>
                          <TableCell className="text-xs">{ef.country}</TableCell>
                          <TableCell className="text-xs">{formatFactorValue(ef.co2e)}</TableCell>
                          <TableCell className="text-xs">{ef.input_unit}</TableCell>
                          <TableCell>
                            <Button 
                              size="sm" 
                              variant={selectedEF?.emission_factor_id === ef.emission_factor_id ? 'default' : 'outline'}
                              onClick={() => handleSelectEF(ef)}
                              data-testid={`select-ef-${ef.emission_factor_id}`}
                            >
                              {selectedEF?.emission_factor_id === ef.emission_factor_id ? 'Selected' : 'Select'}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <div className="p-2 bg-gray-50 text-xs text-muted-foreground border-t">
                  Showing {searchResults.length} results (max 50)
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex justify-end gap-4">
          <Button variant="outline" onClick={() => navigate('/custom-mappings')}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={submitting || !formData.category || !formData.sub_category || !formData.desired_emission_factor_id}
            className="btn-primary"
            data-testid="submit-mapping-btn"
          >
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Submit for Approval
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
